import { useEffect, useRef, useState } from "preact/hooks";
import { onKey } from "../../lib/shortcuts/onKey.js";
import { useShortcutBindings } from "../../lib/shortcuts/useShortcutBindings.js";
import { useEscapeStack } from "../../lib/shortcuts/useEscapeStack.js";

function normalizeOption(option) {
  if (typeof option === "string") {
    return {
      label: option,
      value: option
    };
  }

  return option;
}

function resolveEditorOptions(column, row) {
  const resolvedOptions = typeof column.editor?.options === "function"
    ? column.editor.options(row)
    : column.editor?.options;

  return (resolvedOptions ?? []).map(normalizeOption);
}

function findClosestComboboxOption(options, query) {
  const normalizedQuery = String(query ?? "").trim().toLocaleLowerCase();

  if (!normalizedQuery) {
    return options[0] ?? null;
  }

  return options.find((option) => option.value.toLocaleLowerCase() === normalizedQuery)
    ?? options.find((option) => option.label.toLocaleLowerCase() === normalizedQuery)
    ?? options.find((option) => option.label.toLocaleLowerCase().startsWith(normalizedQuery))
    ?? options.find((option) => option.label.toLocaleLowerCase().includes(normalizedQuery))
    ?? null;
}

export function useDataGridState({
  columns,
  rows,
  rowCount,
  onRowsChange,
  onDraftRowChange,
  rowEditing,
  appendedRowId,
  firstEditableColumnIndex = -1,
  shortcuts = {}
}) {
  const gridRef = useRef(null);
  const editorRef = useRef(null);
  const pendingPreferredRowIdRef = useRef(null);
  const pendingPreferredCellRef = useRef(null);
  const pendingAutoEditColRef = useRef(null);
  const selectedRowIdRef = useRef(null);
  const [selectedCell, setSelectedCell] = useState({ row: 0, col: 0 });
  const [editingCell, setEditingCell] = useState(null);
  const [draftValue, setDraftValue] = useState("");
  const ignoreNextBlurRef = useRef(false);
  const resolvedFirstEditableCol = firstEditableColumnIndex >= 0
    ? firstEditableColumnIndex
    : columns.findIndex((column) => column.editable);
  const duplicateRowShortcutEnabled = shortcuts.duplicateRow !== false;

  useEffect(() => {
    gridRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    if (editingCell && editorRef.current) {
      editorRef.current.focus();
      if (editorRef.current.select) {
        editorRef.current.select();
      }
      if (columns[editingCell.col]?.inputType === "date") {
        editorRef.current.showPicker?.();
      }
    }
  }, [columns, editingCell]);

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) {
      return;
    }

    const activeCell = grid.querySelector(
      `[data-row-index="${selectedCell.row}"][data-col-index="${selectedCell.col}"]`
    );

    activeCell?.scrollIntoView({
      block: "nearest",
      inline: "nearest"
    });

    if (!activeCell) {
      return;
    }

    const header = grid.querySelector("thead");
    const headerHeight = header?.getBoundingClientRect().height ?? 0;
    const gridRect = grid.getBoundingClientRect();
    const cellRect = activeCell.getBoundingClientRect();
    const topBoundary = gridRect.top + headerHeight;

    if (cellRect.top < topBoundary) {
      grid.scrollTop -= (topBoundary - cellRect.top);
      return;
    }

    if (cellRect.bottom > gridRect.bottom) {
      grid.scrollTop += (cellRect.bottom - gridRect.bottom);
    }
  }, [selectedCell, editingCell]);

  useEffect(() => {
    const selectedRowId = selectedRowIdRef.current;
    if (selectedRowId) {
      const selectedRowIndex = rows.findIndex((row) => row.id === selectedRowId);
      if (selectedRowIndex >= 0 && selectedRowIndex !== selectedCell.row) {
        setSelectedCell((current) => ({
          row: selectedRowIndex,
          col: Math.min(current.col, columns.length - 1)
        }));
        return;
      }
    }

    const pendingPreferredRowId = pendingPreferredRowIdRef.current;
    if (!pendingPreferredRowId) {
      const pendingPreferredCell = pendingPreferredCellRef.current;
      const pendingAutoEditCol = pendingAutoEditColRef.current;
      if (pendingPreferredCell) {
        pendingPreferredCellRef.current = null;
        pendingAutoEditColRef.current = null;
        setSelectedCell((current) => ({
          row: current.row,
          col: Math.min(pendingPreferredCell.col ?? current.col, columns.length - 1)
        }));
        requestAnimationFrame(() => {
          if (pendingAutoEditCol === null) {
            gridRef.current?.focus({ preventScroll: true });
            return;
          }

          const nextRow = rows[selectedCell.row];
          const nextColumn = columns[pendingAutoEditCol];
          if (!nextColumn?.editable) {
            gridRef.current?.focus({ preventScroll: true });
            return;
          }

          selectedRowIdRef.current = nextRow?.id ?? null;
          ignoreNextBlurRef.current = false;
          setDraftValue(
            nextColumn.getDraftValue
              ? nextColumn.getDraftValue(nextRow)
              : String(nextRow?.[nextColumn.key] ?? "")
          );
          setEditingCell({ row: selectedCell.row, col: pendingAutoEditCol });
        });
        return;
      }

      if (selectedCell.row >= rowCount && rowCount > 0) {
        setSelectedCell((current) => ({
          row: Math.max(0, rowCount - 1),
          col: Math.min(current.col, columns.length - 1)
        }));
      }
      return;
    }

    const preferredRowIndex = rows.findIndex((row) => row.id === pendingPreferredRowId);
    if (preferredRowIndex >= 0) {
      pendingPreferredRowIdRef.current = null;
      const pendingPreferredCell = pendingPreferredCellRef.current;
      const pendingAutoEditCol = pendingAutoEditColRef.current;
      pendingPreferredCellRef.current = null;
      pendingAutoEditColRef.current = null;
      selectedRowIdRef.current = pendingPreferredRowId;
      setSelectedCell((current) => ({
        row: preferredRowIndex,
        col: Math.min(pendingPreferredCell?.col ?? current.col, columns.length - 1)
      }));
      requestAnimationFrame(() => {
        if (pendingAutoEditCol === null) {
          gridRef.current?.focus({ preventScroll: true });
          return;
        }

        const nextRow = rows[preferredRowIndex];
        const nextColumn = columns[pendingAutoEditCol];
        if (!nextColumn?.editable) {
          gridRef.current?.focus({ preventScroll: true });
          return;
        }

        selectedRowIdRef.current = nextRow?.id ?? null;
        ignoreNextBlurRef.current = false;
        setDraftValue(
          nextColumn.getDraftValue
            ? nextColumn.getDraftValue(nextRow)
            : String(nextRow?.[nextColumn.key] ?? "")
        );
        setEditingCell({ row: preferredRowIndex, col: pendingAutoEditCol });
      });
      return;
    }

    if (selectedCell.row >= rowCount && rowCount > 0) {
      setSelectedCell((current) => ({
        row: Math.max(0, rowCount - 1),
        col: Math.min(current.col, columns.length - 1)
      }));
    }
  }, [columns, rowCount, rows, selectedCell.row]);

  function findEditableColumnIndex(col, direction = "forward") {
    const isForward = direction === "forward";
    const delta = isGridRtl()
      ? (isForward ? 1 : -1)
      : (isForward ? 1 : -1);

    for (
      let nextCol = col + delta;
      nextCol >= 0 && nextCol < columns.length;
      nextCol += delta
    ) {
      if (columns[nextCol]?.editable) {
        return nextCol;
      }
    }

    return null;
  }

  function focusGrid() {
    gridRef.current?.focus({ preventScroll: true });
  }

  function getSelectedCellState() {
    const row = rows[selectedCell.row];
    const column = columns[selectedCell.col];

    if (!row || !column) {
      return null;
    }

    return {
      row,
      column,
      rowIndex: selectedCell.row,
      colIndex: selectedCell.col
    };
  }

  function getClipboardValue(column, row) {
    if (typeof column.getClipboardValue === "function") {
      return String(column.getClipboardValue(row) ?? "");
    }

    if (column.editable && typeof column.getDraftValue === "function") {
      return String(column.getDraftValue(row) ?? "");
    }

    if (typeof column.displayValue === "function") {
      return String(column.displayValue(row) ?? "");
    }

    return String(row?.[column.key] ?? "");
  }

  function normalizePastedValue(column, row, pastedValue) {
    const normalizedValue = String(pastedValue ?? "").replace(/\r\n/g, "\n").split("\n")[0] ?? "";

    if (column.editor?.kind !== "combobox" || column.editor?.allowCustomValue !== false) {
      return normalizedValue;
    }

    const options = resolveEditorOptions(column, row);
    const closestOption = findClosestComboboxOption(options, normalizedValue);
    return closestOption?.value ?? normalizedValue;
  }

  function applyCellDraftValue(rowIndex, colIndex, nextDraftValue) {
    const targetRow = rows[rowIndex];
    const column = columns[colIndex];

    if (!targetRow || !column?.editable) {
      return;
    }

    const nextCellValue = column.parseDraft ? column.parseDraft(nextDraftValue) : nextDraftValue;
    const isAppendedRow = targetRow?.id && targetRow.id === appendedRowId;

    onRowsChange((currentRows) => {
      if (isAppendedRow && rowEditing?.appendEmptyRow) {
        const baseRow = targetRow ?? rowEditing.createEmptyRow();
        const nextRow = column.applyDraft ? column.applyDraft(baseRow, nextCellValue) : {
          ...baseRow,
          [column.key]: nextCellValue
        };

        if (rowEditing.isRowEmpty(nextRow)) {
          onDraftRowChange?.(nextRow);
          selectedRowIdRef.current = nextRow.id ?? null;
          return currentRows;
        }

        onDraftRowChange?.(null);
        selectedRowIdRef.current = nextRow.id ?? null;
        pendingPreferredRowIdRef.current = nextRow.id ?? null;
        pendingPreferredCellRef.current = { col: colIndex };
        pendingAutoEditColRef.current = null;

        return [
          nextRow,
          ...currentRows
        ];
      }

      selectedRowIdRef.current = targetRow?.id ?? null;
      pendingPreferredRowIdRef.current = targetRow?.id ?? null;
      pendingPreferredCellRef.current = { col: colIndex };
      pendingAutoEditColRef.current = null;

      return currentRows.map((entry) => {
        if (targetRow?.id && entry.id !== targetRow.id) {
          return entry;
        }

        if (!targetRow?.id) {
          return entry;
        }

        return column.applyDraft ? column.applyDraft(entry, nextCellValue) : {
          ...entry,
          [column.key]: nextCellValue
        };
      });
    });
  }

  function createDuplicatedRow(sourceRow, sourceRowIndex) {
    if (typeof rowEditing?.createDuplicateRow === "function") {
      return rowEditing.createDuplicateRow(sourceRow, sourceRowIndex);
    }

    return {
      ...sourceRow,
      id: crypto.randomUUID()
    };
  }

  async function copySelectedCell() {
    const cellState = getSelectedCellState();
    if (!cellState || typeof navigator?.clipboard?.writeText !== "function") {
      return;
    }

    await navigator.clipboard.writeText(getClipboardValue(cellState.column, cellState.row));
  }

  async function pasteSelectedCell() {
    const cellState = getSelectedCellState();
    if (!cellState?.column?.editable || typeof navigator?.clipboard?.readText !== "function") {
      return;
    }

    const clipboardText = await navigator.clipboard.readText();
    const nextDraftValue = normalizePastedValue(cellState.column, cellState.row, clipboardText);
    applyCellDraftValue(cellState.rowIndex, cellState.colIndex, nextDraftValue);
    focusGrid();
  }

  function clearSelectedDraftCell() {
    const cellState = getSelectedCellState();
    if (!cellState?.column?.editable || !rowEditing?.appendEmptyRow) {
      return;
    }

    const isDraftRow = !rowEditing.isRowComplete(cellState.row);
    if (!isDraftRow) {
      return;
    }

    applyCellDraftValue(cellState.rowIndex, cellState.colIndex, "");
    focusGrid();
  }

  function duplicateSelectedRow() {
    const cellState = getSelectedCellState();
    const sourceRowId = cellState?.row?.id ?? null;

    if (!sourceRowId) {
      return;
    }

    onRowsChange((currentRows) => {
      const sourceRowIndex = currentRows.findIndex((row) => row.id === sourceRowId);
      if (sourceRowIndex < 0) {
        return currentRows;
      }

      const sourceRow = currentRows[sourceRowIndex];
      const duplicatedRow = createDuplicatedRow(sourceRow, sourceRowIndex);
      if (!duplicatedRow || typeof duplicatedRow !== "object") {
        return currentRows;
      }

      if (!duplicatedRow.id) {
        duplicatedRow.id = crypto.randomUUID();
      }

      const nextEditableCol = resolvedFirstEditableCol >= 0 ? resolvedFirstEditableCol : 0;
      pendingPreferredRowIdRef.current = duplicatedRow.id;
      pendingPreferredCellRef.current = { col: nextEditableCol };
      pendingAutoEditColRef.current = null;
      selectedRowIdRef.current = sourceRowId;

      return [
        ...currentRows.slice(0, sourceRowIndex + 1),
        duplicatedRow,
        ...currentRows.slice(sourceRowIndex + 1)
      ];
    });

    focusGrid();
  }

  function activateCell(row, col) {
    selectedRowIdRef.current = rows[row]?.id ?? null;
    setSelectedCell({ row, col });
    focusGrid();
  }

  function startEdit(row, col, rowData) {
    const column = columns[col];
    if (!column?.editable) {
      return;
    }

    selectedRowIdRef.current = rowData?.id ?? rows[row]?.id ?? null;
    setSelectedCell({ row, col });
    ignoreNextBlurRef.current = false;
    setDraftValue(column.getDraftValue ? column.getDraftValue(rowData) : String(rowData[column.key] ?? ""));
    setEditingCell({ row, col });
  }

  function finishEdit(mode, nextDraftValue = draftValue, targetColOverride = null) {
    if (!editingCell) {
      return;
    }

    const { row, col } = editingCell;
    if (mode === "commit" || mode === "commit-stay") {
      const column = columns[col];
      const nextCellValue = column.parseDraft ? column.parseDraft(nextDraftValue) : nextDraftValue;
      const targetRow = rows[row];
      const isAppendedRow = targetRow?.id && targetRow.id === appendedRowId;
      const isDraftRow = Boolean(rowEditing?.appendEmptyRow) && targetRow && !rowEditing.isRowComplete(targetRow);
      const shouldAdvance = mode === "commit";
      const nextEditableCol = shouldAdvance
        ? (targetColOverride ?? (isDraftRow ? findEditableColumnIndex(col, "forward") : null))
        : null;

      onRowsChange((currentRows) => {
        if (isAppendedRow && rowEditing?.appendEmptyRow) {
          const baseRow = targetRow ?? rowEditing.createEmptyRow();
          const nextRow = column.applyDraft ? column.applyDraft(baseRow, nextCellValue) : {
            ...baseRow,
            [column.key]: nextCellValue
          };

          if (rowEditing.isRowEmpty(nextRow)) {
            onDraftRowChange?.(nextRow);
            pendingPreferredRowIdRef.current = null;
            pendingPreferredCellRef.current = nextEditableCol === null ? { col } : { col: nextEditableCol };
            pendingAutoEditColRef.current = nextEditableCol;
            selectedRowIdRef.current = nextRow.id ?? null;
            return currentRows;
          }

          onDraftRowChange?.(null);
          pendingPreferredRowIdRef.current = nextRow.id ?? null;
          pendingPreferredCellRef.current = { col: nextEditableCol ?? col };
          pendingAutoEditColRef.current = nextEditableCol;
          selectedRowIdRef.current = nextRow.id ?? null;

          return [
            nextRow,
            ...currentRows
          ];
        }

        pendingPreferredRowIdRef.current = targetRow?.id ?? null;
        pendingPreferredCellRef.current = { col: nextEditableCol ?? col };
        pendingAutoEditColRef.current = nextEditableCol;
        selectedRowIdRef.current = targetRow?.id ?? null;

        return currentRows.map((entry) => {
          if (targetRow?.id && entry.id !== targetRow.id) {
            return entry;
          }

          if (!targetRow?.id) {
            return entry;
          }

          return column.applyDraft ? column.applyDraft(entry, nextCellValue) : {
            ...entry,
            [column.key]: nextCellValue
          };
        });
      });
    }

    setSelectedCell({ row, col });
    setEditingCell(null);
    requestAnimationFrame(() => {
      const grid = editorRef.current?.closest('[data-role="data-grid"]') ?? gridRef.current;
      grid?.focus({ preventScroll: true });
    });
  }

  function commitEdit(nextValue = draftValue) {
    setDraftValue(nextValue);
    ignoreNextBlurRef.current = true;
    finishEdit("commit", nextValue);
    editorRef.current?.blur();
  }

  function commitEditAndMove(direction, nextValue = draftValue) {
    const targetCol = editingCell ? findEditableColumnIndex(editingCell.col, direction) : null;
    if (targetCol === null) {
      setDraftValue(nextValue);
      ignoreNextBlurRef.current = true;
      finishEdit("commit-stay", nextValue);
      editorRef.current?.blur();
      return;
    }

    setDraftValue(nextValue);
    ignoreNextBlurRef.current = true;
    finishEdit("commit", nextValue, targetCol);
    editorRef.current?.blur();
  }

  function cancelEdit() {
    ignoreNextBlurRef.current = true;
    finishEdit("cancel");
    editorRef.current?.blur();
  }

  function moveSelection(rowOffset, colOffset) {
    const row = selectedCell.row;
    const col = selectedCell.col;
    const nextRow = Math.max(0, Math.min(rowCount - 1, row + rowOffset));
    const nextCol = Math.max(0, Math.min(columns.length - 1, col + colOffset));
    selectedRowIdRef.current = rows[nextRow]?.id ?? null;
    setSelectedCell({ row: nextRow, col: nextCol });
  }

  function moveSelectionTo(row, col) {
    const nextRow = Math.max(0, Math.min(rowCount - 1, row));
    selectedRowIdRef.current = rows[nextRow]?.id ?? null;
    setSelectedCell({
      row: nextRow,
      col: Math.max(0, Math.min(columns.length - 1, col))
    });
  }

  function isGridRtl() {
    if (!gridRef.current) {
      return false;
    }

    return window.getComputedStyle(gridRef.current).direction === "rtl";
  }

  const gridBindings = (() => {
    if (editingCell) {
      return [];
    }

    const row = selectedCell.row;
    const col = selectedCell.col;

    return [
      onKey("Control+ArrowUp", () => moveSelectionTo(0, col)),
      onKey("Control+ArrowDown", () => moveSelectionTo(Math.max(0, rowCount - 1), col)),
      onKey("Control+ArrowLeft", () => moveSelectionTo(
        row,
        isGridRtl() ? Math.max(0, columns.length - 1) : 0
      )),
      onKey("Control+ArrowRight", () => moveSelectionTo(
        row,
        isGridRtl() ? 0 : Math.max(0, columns.length - 1)
      )),
      onKey("ArrowUp", () => moveSelection(-1, 0)),
      onKey("ArrowDown", () => moveSelection(1, 0)),
      onKey("ArrowLeft", () => moveSelection(0, isGridRtl() ? 1 : -1)),
      onKey("ArrowRight", () => moveSelection(0, isGridRtl() ? -1 : 1)),
      onKey("Tab", () => moveSelection(0, isGridRtl() ? -1 : 1), { allowInInput: true }),
      onKey("Shift+Tab", () => moveSelection(0, isGridRtl() ? 1 : -1), { allowInInput: true }),
      onKey("$mod+c", async () => {
        await copySelectedCell();
      }),
      onKey("$mod+v", async () => {
        await pasteSelectedCell();
      }),
      duplicateRowShortcutEnabled ? onKey("Alt+C", () => {
        duplicateSelectedRow();
      }) : null,
      onKey("Delete", () => {
        clearSelectedDraftCell();
      }),
      onKey("Enter", () => startEdit(row, col, rows[row])),
      rowEditing?.allowDeleteRow ? onKey("Control+Delete", () => {
          const targetRow = rows[row];
          if (!targetRow || rowEditing.isRowEmpty(targetRow)) {
            return;
          }

          pendingPreferredRowIdRef.current = targetRow.id ?? null;
          onRowsChange((currentRows) => {
            if (targetRow.id) {
              return currentRows.filter((entry) => entry.id !== targetRow.id);
            }

            return currentRows;
          });
        }) : null
    ].filter(Boolean);
  })();

  const editorBindings = (() => {
    if (!editingCell || columns[editingCell.col]?.editor?.kind === "combobox") {
      return [];
    }

    return [
      onKey("Enter", () => {
          commitEdit();
        }, { allowInInput: true }),
      onKey("Tab", () => {
          commitEditAndMove("forward");
        }, { allowInInput: true }),
      onKey("Shift+Tab", () => {
          commitEditAndMove("backward");
        }, { allowInInput: true })
    ];
  })();

  useEscapeStack(() => {
    if (!editingCell || columns[editingCell.col]?.editor?.kind === "combobox") {
      return false;
    }

    cancelEdit();
    return true;
  }, Boolean(editingCell) && columns[editingCell.col]?.editor?.kind !== "combobox");

  useShortcutBindings(gridRef, gridBindings);
  useShortcutBindings(editorRef, editorBindings);

  function handleEditorBlur(nextValue = draftValue) {
    if (ignoreNextBlurRef.current) {
      ignoreNextBlurRef.current = false;
      return;
    }

    finishEdit("commit-stay", nextValue);
  }

  function cellClasses(rowIndex, colIndex, column, row) {
    const cellState = column.cellState?.(row) ?? "";

    return [
      "cell",
      column.align,
      column.editable ? "editable" : "readonly",
      row?.archived ? "archived" : "",
      cellState ? `state-${cellState}` : "",
      selectedCell.row === rowIndex && selectedCell.col === colIndex ? "active" : "",
      editingCell?.row === rowIndex && editingCell?.col === colIndex ? "editing" : "",
      column.type === "meter" ? "meter" : ""
    ]
      .filter(Boolean)
      .join(" ");
  }

  return {
    gridRef,
    editorRef,
    selectedCell,
    editingCell,
    draftValue,
    setDraftValue,
    activateCell,
    startEdit,
    moveSelectionTo,
    commitEdit,
    commitEditAndMove,
    cancelEdit,
    handleEditorBlur,
    duplicateSelectedRow,
    cellClasses
  };
}
